import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@api'
import { appendAvatarToFormData, showSuccessToast } from '@utils'
import { CreateGhostProfileData } from './useCreateGhostProfile'
import { useLanguage } from '@context'

export interface UpdateGhostProfileData extends CreateGhostProfileData {
  id: string
}

const updateGhostProfile = async (data: UpdateGhostProfileData): Promise<void> => {
  const { id, avatar, contacts, languages, nationalities, availabilities, hashtags, pinsHashtags, pinsCategories, goals, hobbies, skills, professionIds, residenceHistory, ...otherData } = data
  const formData = new FormData()

  // Add simple fields
  if (otherData.scope) {
    formData.append('scope', otherData.scope)
  }
  if (otherData.gender) {
    formData.append('gender', otherData.gender)
  }
  if (otherData.residence !== undefined) {
    formData.append('residence', otherData.residence || '')
  }
  if (otherData.birthDate) {
    formData.append('birthDate', otherData.birthDate)
  }
  if (otherData.bio) {
    formData.append('bio', otherData.bio)
  }
  if (otherData.company !== undefined) {
    formData.append('company', otherData.company)
  }
  if (otherData.firstName) {
    formData.append('firstName', otherData.firstName)
  }
  if (otherData.lastName) {
    formData.append('lastName', otherData.lastName)
  }
  if (otherData.gender) {
    formData.append('gender', otherData.gender)
  }
  if (otherData.origins !== undefined) {
    if (otherData.origins.length > 0) {
      otherData.origins.forEach((origin, index) => {
        formData.append(`origins[${index}]`, origin)
      })
    } else {
      formData.append('origins', JSON.stringify([]))
    }
  }


  if(hashtags !== undefined) {
    if (hashtags.length > 0) {
      hashtags.forEach((tag, index) => {
        formData.append(`hashtags[${index}]`, tag)
      })
    } else {
      formData.append('hashtags', JSON.stringify([]))
    }
  }

  // Handle hashtags
  if (pinsHashtags !== undefined) {
    if (pinsHashtags.length > 0) {
      pinsHashtags.forEach((tag, index) => {
        formData.append(`pinsHashtags[${index}]`, tag)
      })
    } else {
      formData.append('pinsHashtags', JSON.stringify([]))
    }
  }

  if(pinsCategories !== undefined) {
    if (pinsCategories.length > 0) {
      pinsCategories.forEach((category, index) => {
        formData.append(`pinsCategories[${index}]`, category)
      })
    } else {
      formData.append('pinsCategories', JSON.stringify([]))
    }
  }

  // Handle goals, hobbies, skills
  if (goals !== undefined) {
    if (goals.length > 0) {
      goals.forEach((goal, index) => {
        formData.append(`goals[${index}]`, goal)
      })
    } else {
      formData.append('goals', JSON.stringify([]))
    }
  }
  if (hobbies !== undefined) {
    if (hobbies.length > 0) {
      hobbies.forEach((hobby, index) => {
        formData.append(`hobbies[${index}]`, hobby)
      })
    } else {
      formData.append('hobbies', JSON.stringify([]))
    }
  }
  if (skills !== undefined) {
    if (skills.length > 0) {
      skills.forEach((skill, index) => {
        formData.append(`skills[${index}]`, skill)
      })
    } else {
      formData.append('skills', JSON.stringify([]))
    }
  }

  // Handle languages
  if (languages && languages.length > 0) {
    languages.forEach((language, index) => {
      formData.append(`languages[${index}]`, JSON.stringify({ code: language, level: 'fluent' }))
    })
  } else {
    formData.append('languages', JSON.stringify([]))
  }

  // Handle nationalities
  if (nationalities !== undefined) {
    if (nationalities.length > 0) {
      nationalities.forEach((nationality, index) => {
        formData.append(`nationalities[${index}]`, nationality)
      })
    } else {
      formData.append('nationalities', JSON.stringify([]))
    }
  }

  // Handle residenceHistory
  if (residenceHistory !== undefined) {
    const residenceHistoryArray = residenceHistory.map(residence => ({
      city: residence,
      since: new Date().toISOString(),
      until: null,
      address: '',
      country: null,
      isCurrent: false,
    }))
    formData.append('residenceHistory', JSON.stringify(residenceHistoryArray))
  }

  // Handle professionIds
  if (professionIds !== undefined) {
    if (professionIds.length > 0) {
      professionIds.forEach((professionId, index) => {
        formData.append(`professionIds[${index}]`, professionId)
      })
    } else {
      formData.append('professionIds', JSON.stringify([]))
    }
  }

  // Handle contacts
  if (contacts !== undefined) {
    formData.append('contacts', JSON.stringify(contacts))
  }

  // Handle availabilities
  if (availabilities !== undefined) {
    formData.append('availabilities', JSON.stringify(availabilities))
  }

  // Handle avatar
  if (avatar) {
    appendAvatarToFormData(formData, avatar.uri, 'avatar')
  }

  await api.patch(`/ghost-profiles/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

export const useUpdateGhostProfile = () => {
  const queryClient = useQueryClient()
  const { t } = useLanguage()

  return useMutation<void, Error, UpdateGhostProfileData>({
    mutationFn: updateGhostProfile,
    onSuccess: async (_, variables) => {
      showSuccessToast(t('toast.success'), t('ghostProfile.updateSuccess'))
      // Invalidate contacts query and profile query
      await queryClient.invalidateQueries({ queryKey: ['contacts'] })
      await queryClient.invalidateQueries({ queryKey: ['profile', variables.id] })
    },
  })
}

